import { FiGitMerge } from 'react-icons/fi';

export const relativeClausesData = {
  courseId: 'relative-clauses',
  courseType: 'grammar',
  title: 'Relative Clauses',
  icon: FiGitMerge,
  overview: {
    impact: 'Shows grammatical sophistication for Band 7+',
    focus: 'Using who, which, that, whose, where, when',
    uses: 'Writing Task 1 & 2, Speaking',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Relative Pronouns',
      content: `<p>Relative pronouns connect clauses and add information about nouns:</p>`,
      table: {
        headers: ['Pronoun', 'Used For', 'Example'],
        rows: [
          ['who', 'People', 'The student who studies hard will pass.'],
          ['which', 'Things/Animals', 'The book which I read was excellent.'],
          ['that', 'People/Things', 'The car that he bought is new.'],
          ['whose', 'Possession', 'The woman whose bag was stolen reported it.'],
          ['where', 'Places', 'The city where I was born is beautiful.'],
          ['when', 'Time', 'The day when we met was special.'],
        ],
      },
    },
    {
      title: 'Defining vs Non-Defining Clauses',
      content: `<p>This distinction affects meaning and punctuation:</p>`,
      examples: [
        { label: 'Defining', text: 'Students who work hard succeed. (Only hardworking students succeed)', translation: 'No commas - essential information' },
        { label: 'Non-defining', text: 'My brother, who lives in London, is a doctor. (Extra information about my only brother)', translation: 'Commas needed - extra information' },
      ],
      warning: 'Non-defining clauses need commas! Forgetting them can change the meaning.',
    },
    {
      title: 'Reduced Relative Clauses',
      content: `<p>You can shorten relative clauses for more sophisticated writing:</p>`,
      examples: [
        { label: 'Full', text: 'The man who is standing there is my teacher.' },
        { label: 'Reduced', text: 'The man standing there is my teacher.' },
        { label: 'Full', text: 'The documents which were signed yesterday are ready.' },
        { label: 'Reduced', text: 'The documents signed yesterday are ready.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Correct Relative Pronoun',
      type: 'select',
      instructions: 'Select the appropriate relative pronoun.',
      questions: [
        {
          text: 'The woman ___ won the prize is my neighbor.',
          options: ['who', 'which', 'whose'],
          answer: 'who',
          explanation: '"Who" is used for people as the subject of the clause.',
        },
        {
          text: 'The book ___ you recommended was excellent.',
          options: ['which', 'who', 'whose'],
          answer: 'which',
          explanation: '"Which" is used for things. "That" could also work here.',
        },
        {
          text: 'The student ___ essay won first prize is very talented.',
          options: ['whose', 'who', 'which'],
          answer: 'whose',
          explanation: '"Whose" shows possession (the student\'s essay).',
        },
        {
          text: 'The city ___ I grew up is now very modern.',
          options: ['where', 'which', 'when'],
          answer: 'where',
          explanation: '"Where" is used for places.',
        },
        {
          text: 'The day ___ we first met was unforgettable.',
          options: ['when', 'where', 'which'],
          answer: 'when',
          explanation: '"When" is used for time expressions.',
        },
        {
          text: 'The manager ___ I spoke to was very helpful.',
          options: ['who', 'which', 'whose'],
          answer: 'who',
          explanation: '"Who" for people. Note: "whom" is also correct but less common.',
        },
      ],
    },
    {
      title: 'Defining or Non-Defining?',
      type: 'select',
      instructions: 'Identify whether the relative clause is defining (essential) or non-defining (extra information).',
      questions: [
        {
          text: 'My brother who lives in Paris is a doctor.',
          options: ['Defining', 'Non-defining'],
          answer: 'Defining',
          explanation: 'No commas = defining. This implies you have multiple brothers and are specifying which one.',
        },
        {
          text: 'My brother, who lives in Paris, is a doctor.',
          options: ['Non-defining', 'Defining'],
          answer: 'Non-defining',
          explanation: 'Commas = non-defining. This adds extra info about your only brother.',
        },
        {
          text: 'Students who study hard usually succeed.',
          options: ['Defining', 'Non-defining'],
          answer: 'Defining',
          explanation: 'Defining clause—it specifies which students succeed (only the hardworking ones).',
        },
        {
          text: 'The Eiffel Tower, which was built in 1889, is a famous landmark.',
          options: ['Non-defining', 'Defining'],
          answer: 'Non-defining',
          explanation: 'The date is extra information—we already know which tower we mean.',
        },
      ],
    },
    {
      title: 'Add Punctuation',
      type: 'select',
      instructions: 'Does this sentence need commas around the relative clause?',
      questions: [
        {
          text: 'Albert Einstein who developed the theory of relativity was born in Germany.',
          options: ['Yes, needs commas', 'No commas needed'],
          answer: 'Yes, needs commas',
          explanation: 'There\'s only one Einstein, so the clause is non-defining (extra info).',
        },
        {
          text: 'The employees who work overtime will receive bonuses.',
          options: ['No commas needed', 'Yes, needs commas'],
          answer: 'No commas needed',
          explanation: 'Defining clause—it specifies which employees (only overtime workers) get bonuses.',
        },
        {
          text: 'Tokyo which is the capital of Japan has a population of over 13 million.',
          options: ['Yes, needs commas', 'No commas needed'],
          answer: 'Yes, needs commas',
          explanation: 'There\'s only one Tokyo, so being the capital is extra information.',
        },
      ],
    },
    {
      title: 'Combine Sentences',
      type: 'writing',
      instructions: 'Combine these sentences using a relative clause.',
      questions: [
        {
          text: 'The scientist won a Nobel Prize. She discovered a new treatment for cancer.',
          example: 'The scientist who discovered a new treatment for cancer won a Nobel Prize.',
          tip: 'Use "who" for the scientist. Place the relative clause after the noun it describes.',
        },
        {
          text: 'I visited the museum. The famous painting is displayed there.',
          example: 'I visited the museum where the famous painting is displayed.',
          tip: 'Use "where" for places.',
        },
        {
          text: 'The company is expanding rapidly. Its products are sold worldwide.',
          example: 'The company whose products are sold worldwide is expanding rapidly.',
          tip: 'Use "whose" to show possession (the company\'s products).',
        },
      ],
    },
    {
      title: 'Reduce the Relative Clause',
      type: 'writing',
      instructions: 'Shorten these sentences by reducing the relative clause.',
      questions: [
        {
          text: 'The report which was published yesterday contains important findings.',
          example: 'The report published yesterday contains important findings.',
          tip: 'Remove "which was" to create a reduced relative clause.',
        },
        {
          text: 'People who are living in coastal areas face flood risks.',
          example: 'People living in coastal areas face flood risks.',
          tip: 'Remove "who are" when followed by an -ing verb.',
        },
        {
          text: 'The documents that were signed by the CEO are confidential.',
          example: 'The documents signed by the CEO are confidential.',
          tip: 'Remove "that were" before past participles.',
        },
      ],
    },
  ],
  tips: [
    'Use "who" for people, "which" for things',
    'Practice non-defining clauses with correct punctuation',
    'Try reducing relative clauses in your writing',
  ],
  dailyPractice: [
    'Combine sentences using relative pronouns',
    'Practice defining vs non-defining clauses',
    'Reduce relative clauses in your writing',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Relative Clauses: Clauses that modify nouns using who, which, that, whose, where, when. Includes defining and non-defining clauses with punctuation rules.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...relativeClausesData,
      title: relativeClausesData.title + ' (Foundations)',
      overview: {
        ...relativeClausesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: relativeClausesData.sections.slice(0, Math.max(2, Math.ceil(relativeClausesData.sections.length / 2))),
      exercises: relativeClausesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...relativeClausesData,
      title: relativeClausesData.title + ' (Advanced)',
      overview: {
        ...relativeClausesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: relativeClausesData.sections,
      exercises: relativeClausesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(relativeClausesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return relativeClausesData;
}
